import type { SettingsTab } from '@/lib/constants';
import type { IntegrationType } from '@/lib/integrations/settings-pages';
import type { Route } from 'next';

import { settingsTabForIntegration } from '@/lib/integrations/settings-pages';
import { buildSettingsPath } from '@/utils/app-paths';

export const SETTINGS_TAB_TITLES: Record<SettingsTab, string> = {
  'github': 'GitHub',
  'notifications': 'Notifications',
  'integrations': 'Integrations',
  'api-cli': 'API, CLI & MCP',
  'members': 'Members',
  'billing': 'Billing',
  'usage': 'Usage',
  'audit-log': 'Audit log',
};

export const INTEGRATION_PAGE_TITLES: Record<IntegrationType, string> = {
  'linear': 'Linear',
  'jira': 'Jira',
  'asana': 'Asana',
  'github-issues': 'GitHub Issues',
  // The MCP servers an org adds for cubic's agents. Named apart from cubic's own MCP server
  // below, which other agents connect to and so works the other way round.
  'mcp-servers': 'MCP servers',
  'cursor-cloud-agent': 'Cursor cloud agents',
  'posthog': 'PostHog',
  'sentry': 'Sentry',
  'datadog': 'Datadog',
  'chatgpt-plan': 'ChatGPT plan',
  'notion': 'Notion',
  'confluence': 'Confluence',
  'slack': 'Slack',
  'mcp': 'cubic MCP server',
  'cli': 'cubic CLI',
  'api': 'cubic API',
};

/** The DOM ids settings search scrolls to. The elements take their ids from here too. */
export const SETTINGS_ANCHORS = {
  GITHUB_ACCOUNTS: 'github-accounts',
  SLACK_CONNECTION: 'slack-connection',
  PULL_REQUEST_NOTIFICATIONS: 'pull-request-notifications',
  CODEBASE_SCAN_NOTIFICATIONS: 'codebase-scan-notifications',
  CUBIC_CHANGELOG: 'cubic-changelog',
  SLACK_USER_MAPPINGS: 'slack-user-mappings',
  ASANA_WORKSPACE: 'asana-workspace',
  MCP_SERVERS_LIST: 'mcp-servers-list',
  MCP_SERVER_LIBRARY: 'mcp-server-library',
  CURSOR_AUTHENTICATION: 'cursor-authentication',
  PERSONAL_API_KEY: 'personal-api-key',
  CONNECTED_APPS: 'connected-apps',
  ORGANIZATION_API_KEYS: 'organization-api-keys',
  // `mcp-server-url` is the id of the URL field in the MCP servers page's add dialog.
  MCP_SERVER_URL: 'cubic-mcp-server-url',
  MCP_OAUTH_FALLBACK: 'mcp-oauth-fallback',
  MCP_TOOLS: 'mcp-tools',
  CLI_GET_STARTED: 'cli-get-started',
  CLI_CI_AND_CLOUD_AGENTS: 'cli-ci-and-cloud-agents',
  MEMBERS_API: 'members-api',
  ANALYTICS_API: 'analytics-api',
  TEAM_MEMBERS: 'team-members',
  // `auto-assign-seats` is the id of the setting's switch.
  AUTO_ASSIGN_SEATS: 'auto-assign-seats-setting',
  PLAN: 'plan',
  FLEX_CAPACITY: 'flex-capacity',
  CANCEL_PLAN: 'cancel-plan',
  CODE_REVIEW_USAGE: 'code-review-usage',
  USAGE_CHART: 'usage-chart',
  USAGE_BREAKDOWN: 'usage-breakdown',
} as const;

type SettingsAnchor = (typeof SETTINGS_ANCHORS)[keyof typeof SETTINGS_ANCHORS];

// PostHog opens for two allowlisted installations, the ChatGPT plan where its provider's rollout
// reaches one of the person's installations, and the Audit log tab shows only to admins, so search
// leaves them out.
type IndexedPage =
  | { tab: Exclude<SettingsTab, 'audit-log'>; integration?: undefined }
  | { integration: Exclude<IntegrationType, 'posthog' | 'chatgpt-plan'>; tab?: undefined };

/**
 * Words that find a page or setting besides its title and location, comma-separated so each one
 * stays on one line below.
 */
type Keywords = string;

/** The pages search lists, in sidebar order. */
const PAGES: readonly (IndexedPage & { keywords: Keywords })[] = [
  {
    tab: 'github',
    keywords: 'add a repository, connect github, repositories, integration, connection',
  },
  { tab: 'notifications', keywords: 'slack, direct messages, dm' },
  {
    tab: 'integrations',
    keywords: 'connect, slack, linear, jira, asana, notion, confluence, tools, services',
  },
  { tab: 'api-cli', keywords: 'api keys, developer, token' },
  {
    tab: 'members',
    keywords: 'team, people, seats, roles, bots, subscription',
  },
  {
    tab: 'billing',
    keywords:
      'payment, payment method, card, billing email, billing portal, plan, pricing, upgrade, downgrade, cancel, free, pro, max, enterprise, subscription, invoices, receipts',
  },
  {
    tab: 'usage',
    keywords: 'quota, reviewed lines, review line, limits, rate limit, capacity, codex, credits',
  },
  { integration: 'slack', keywords: 'set up slack notifications, notifications' },
  { integration: 'linear', keywords: 'issues' },
  { integration: 'jira', keywords: 'issues' },
  { integration: 'asana', keywords: 'tasks' },
  { integration: 'github-issues', keywords: 'linked issues, closing keywords' },
  { integration: 'notion', keywords: 'docs, documentation' },
  { integration: 'confluence', keywords: 'docs, documentation' },
  { integration: 'mcp-servers', keywords: 'custom mcp, agent tools, sentry, stripe' },
  { integration: 'cursor-cloud-agent', keywords: 'coding agent, fix provider' },
  { integration: 'sentry', keywords: 'production evidence, errors, logs, codebase scan' },
  { integration: 'datadog', keywords: 'production evidence, logs, codebase scan' },
  {
    integration: 'mcp',
    keywords: 'model context protocol, claude desktop, windsurf, codex, opencode',
  },
  { integration: 'cli', keywords: 'command line, terminal' },
  { integration: 'api', keywords: 'rest api, members, analytics' },
];

/** Every setting search can open, by the id of the element that holds it, in page order. */
const SETTINGS: Record<SettingsAnchor, IndexedPage & { title: string; keywords: Keywords }> = {
  'github-accounts': {
    tab: 'github',
    title: 'GitHub accounts',
    keywords: 'add a repository, repositories, install, github app, organization',
  },
  'pull-request-notifications': {
    tab: 'notifications',
    title: 'Pull request notifications',
    keywords: 'direct messages, dm',
  },
  'codebase-scan-notifications': {
    tab: 'notifications',
    title: 'Codebase scan notifications',
    keywords: 'scan alerts, dm',
  },
  'personal-api-key': {
    tab: 'api-cli',
    title: 'Personal API key',
    keywords: 'cbk, token, generate, regenerate, revoke',
  },
  'connected-apps': {
    tab: 'api-cli',
    title: 'Connected apps',
    keywords: 'oauth, mcp clients, disconnect app, change access',
  },
  'organization-api-keys': {
    tab: 'api-cli',
    title: 'Organization API keys',
    keywords: 'cok, service account, create api key, revoke',
  },
  'auto-assign-seats-setting': {
    tab: 'members',
    title: 'Give new developers a seat automatically',
    keywords: 'auto-assign seats, seat automation',
  },
  'team-members': {
    tab: 'members',
    title: 'People',
    keywords:
      'members, roles, remove member, seat status, bots, teams, work email, export members csv',
  },
  'plan': {
    tab: 'billing',
    title: 'Plan',
    keywords: 'upgrade, downgrade, change plan, change seats, seats, next invoice, pricing',
  },
  'flex-capacity': {
    tab: 'billing',
    title: 'Flex capacity',
    keywords:
      'flex limit, spend limit, monthly limit, maximum monthly spend, overage, top up, top-up size',
  },
  'cancel-plan': {
    tab: 'billing',
    title: 'Cancel plan',
    keywords:
      'cancel, cancel subscription, end subscription, stop billing, unsubscribe, downgrade to free',
  },
  'code-review-usage': {
    tab: 'usage',
    title: 'Code review usage',
    keywords: 'quota, reviewed lines, limit, get more usage, flex budget',
  },
  // The Auto-fix card shows only for allowlisted installations, so search leaves it out, as
  // it does PostHog.
  'usage-chart': { tab: 'usage', title: 'Usage over this period', keywords: 'daily usage, chart' },
  'usage-breakdown': {
    tab: 'usage',
    title: 'Where your usage went',
    keywords: 'breakdown, by repository, by author, export csv, skip test files',
  },
  'slack-connection': {
    integration: 'slack',
    title: 'Slack notifications',
    keywords: 'connect slack, disconnect, reconnect, workspace',
  },
  'cubic-changelog': {
    integration: 'slack',
    title: 'cubic changelog',
    keywords: 'rss, feed, product updates, release notes',
  },
  'slack-user-mappings': {
    integration: 'slack',
    title: 'User mappings',
    keywords: 'map users, auto map, github users, slack users, unmapped',
  },
  'asana-workspace': { integration: 'asana', title: 'Workspace', keywords: 'asana workspace' },
  'mcp-servers-list': {
    integration: 'mcp-servers',
    title: 'Servers',
    keywords: 'tools, replace key, remove server',
  },
  'mcp-server-library': {
    integration: 'mcp-servers',
    title: 'Add a server',
    keywords: 'server library, custom server',
  },
  'cursor-authentication': {
    integration: 'cursor-cloud-agent',
    title: 'Authentication',
    keywords: 'cursor api key, key type',
  },
  'cubic-mcp-server-url': {
    integration: 'mcp',
    title: 'Server URL',
    keywords: 'endpoint, setup prompt',
  },
  'mcp-oauth-fallback': {
    integration: 'mcp',
    title: "Can't sign in with OAuth?",
    keywords: 'personal api key, bearer token',
  },
  'mcp-tools': { integration: 'mcp', title: 'What cubic adds', keywords: 'tools, tool list' },
  'cli-get-started': {
    integration: 'cli',
    title: 'Get started',
    keywords: 'install, sign in, macos, windows',
  },
  'cli-ci-and-cloud-agents': {
    integration: 'cli',
    title: 'CI and cloud agents',
    keywords: 'CUBIC_API_KEY, environment variable, github actions, powershell',
  },
  'members-api': { integration: 'api', title: 'Members API', keywords: 'seats api, roles, rest' },
  'analytics-api': { integration: 'api', title: 'Analytics API', keywords: 'metrics, export' },
};

type IssueContextOptions = {
  /** What the integration links to pull requests, as its settings name them. */
  entityName: string;
  /** Hide when the provider links issues itself (GitHub Issues). */
  showAutoAssociateToggle: boolean;
  showLinkedIssueAnalysisToggle: boolean;
};

const ISSUE_TRACKER: IssueContextOptions = {
  entityName: 'issues',
  showAutoAssociateToggle: true,
  showLinkedIssueAnalysisToggle: true,
};

const DOCUMENTATION: IssueContextOptions = {
  entityName: 'pages',
  showAutoAssociateToggle: true,
  showLinkedIssueAnalysisToggle: false,
};

/** The subpages that render the issue-context settings template, and what each one shows. */
export const ISSUE_CONTEXT_INTEGRATIONS = {
  'linear': ISSUE_TRACKER,
  'jira': ISSUE_TRACKER,
  'asana': ISSUE_TRACKER,
  'github-issues': { ...ISSUE_TRACKER, showAutoAssociateToggle: false },
  'notion': DOCUMENTATION,
  'confluence': DOCUMENTATION,
} satisfies Partial<Record<IntegrationType, IssueContextOptions>>;

export type IssueContextIntegration = keyof typeof ISSUE_CONTEXT_INTEGRATIONS;

function isIssueContextIntegration(
  integration: IntegrationType | undefined,
): integration is IssueContextIntegration {
  return integration !== undefined && integration in ISSUE_CONTEXT_INTEGRATIONS;
}

type SettingDefinition = { anchor: string; title: string; keywords: readonly string[] };

/** An issue-context subpage's settings cards in page order. A card the page hides is null. */
export function getIssueContextCards(integration: IssueContextIntegration) {
  const { entityName, showAutoAssociateToggle, showLinkedIssueAnalysisToggle } =
    ISSUE_CONTEXT_INTEGRATIONS[integration];
  const card = (name: string, title: string, keyword: string): SettingDefinition => ({
    anchor: `${integration}-${name}`,
    title,
    keywords: [keyword],
  });
  return {
    autoAssociate: showAutoAssociateToggle
      ? card('auto-associate', `Automatically associate ${entityName} from PR details`, 'link')
      : null,
    reviewContext: card('review-context', 'Use context when reviewing code', 'ai review'),
    linkedIssueAnalysis: showLinkedIssueAnalysisToggle
      ? card(
          'linked-issue-analysis',
          'Show linked issue analysis in reviews',
          'acceptance criteria',
        )
      : null,
    prDescriptionContext: card(
      'pr-description-context',
      'Use context when writing PR descriptions',
      'pr description',
    ),
  };
}

export type SettingsSearchEntry = {
  /** Unique. The entry's command palette id ends with it. */
  id: string;
  /** The label the page shows. */
  title: string;
  /** "Settings" for a tab, the owning tab for a subpage, and the page for a setting. */
  location: string;
  keywords: readonly string[];
  href: Route;
  /** The DOM id to reveal. Settings only. */
  anchor?: string;
};

// The command palette's old commands for integration pages matched these words.
const SUBPAGE_KEYWORDS = ['configuration', 'config', 'integration', 'settings'];

function splitKeywords(keywords: Keywords) {
  return keywords.split(/\s*,\s*/);
}

function isSamePage(page: IndexedPage, other: IndexedPage) {
  return page.tab === other.tab && page.integration === other.integration;
}

/** Every settings page and individual setting that search and the command palette can open. */
export const SETTINGS_SEARCH_ENTRIES: readonly SettingsSearchEntry[] = PAGES.flatMap(
  ({ keywords, ...page }) => {
    const title = page.integration
      ? INTEGRATION_PAGE_TITLES[page.integration]
      : SETTINGS_TAB_TITLES[page.tab];
    const owner = page.integration
      ? SETTINGS_TAB_TITLES[settingsTabForIntegration(page.integration)]
      : 'Settings';
    const settings: SettingDefinition[] = [
      ...Object.entries(SETTINGS)
        .filter(([, setting]) => isSamePage(setting, page))
        .map(([anchor, setting]) => ({
          anchor,
          title: setting.title,
          keywords: splitKeywords(setting.keywords),
        })),
      ...(isIssueContextIntegration(page.integration)
        ? Object.values(getIssueContextCards(page.integration)).filter((card) => card !== null)
        : []),
    ];
    return [
      {
        id: page.integration ?? page.tab,
        title,
        location: owner,
        keywords: [...splitKeywords(keywords), ...(page.integration ? SUBPAGE_KEYWORDS : [])],
        href: buildSettingsPath(page),
      },
      ...settings.map(({ anchor, ...setting }) => ({
        ...setting,
        id: anchor,
        anchor,
        // The page's title, such as "Members", or "Integrations › Slack" on a subpage.
        location: page.integration ? `${owner} › ${title}` : title,
        href: buildSettingsPath({ ...page, hash: anchor }),
      })),
    ];
  },
);
